import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { api, extractErrorCode } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import {
  Wallet,
  TrendingUp,
  Banknote,
  Copy,
  Check,
  Share2,
  Loader2,
  Sparkles,
  Megaphone,
} from "lucide-react";
import { toast } from "sonner";

const TIER_LABEL = { free: "Free", starter: "Starter", pro: "Pro", business: "Business" };

const STATUS_BADGE = {
  pending: { cls: "tier-free", labelKey: "partner.status_pending" },
  ready_for_payout: { cls: "tier-pro", labelKey: "partner.status_ready_for_payout" },
  paid: { cls: "tier-business", labelKey: "partner.status_paid" },
};

export default function Partner() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const load = async () => {
    const r = await api.get("/affiliate/dashboard");
    setData(r.data);
  };

  useImperativeLoad(() => { load(); }, []);

  // Redirect to /affiliate if not an approved affiliate
  if (user && (!user.is_affiliate || user.affiliate_status !== "approved")) {
    return <Navigate to="/affiliate" replace />;
  }

  if (!data) {
    return <div className="flex h-full items-center justify-center text-slate-400">{t("common.loading")}</div>;
  }

  const link = `${window.location.origin}${data.referral_path}`;
  const handleCopy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success(t("partner.link_copied"));
    setTimeout(() => setCopied(false), 1500);
  };
  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "NEURACOM by TelcaVoIP", url: link }); } catch { /* noop */ }
    } else {
      handleCopy();
    }
  };
  const handlePayoutRequest = async () => {
    setRequesting(true);
    try {
      await api.post("/affiliate/request-payout");
      toast.success(t("partner.payout_requested"));
      load();
    } catch (err) {
      toast.error(`Error: ${extractErrorCode(err)}`);
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="px-8 py-10 lg:px-12" data-testid="partner-page">
      <header className="mb-8">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-[#9ACD32]/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#9ACD32]">
          <Sparkles className="h-3 w-3" />
          PARTNER · {Math.round((data.commission_rate || 0.2) * 100)}%
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-white">
          {t("partner.title")}
        </h1>
        <p className="mt-2 text-slate-300">{t("partner.subtitle")}</p>
      </header>

      <Card className="mb-8 border-slate-800 bg-[#0A0C1F] p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            {t("partner.your_link")}
          </p>
          <Link to="/partner/kit">
            <Button variant="outline" size="sm" className="rounded-full border-[#9ACD32]/40 bg-[#9ACD32]/5 text-xs font-bold text-[#9ACD32] hover:bg-[#9ACD32]/10" data-testid="partner-open-kit-btn">
              <Megaphone className="mr-1.5 h-3 w-3" />
              {t("partner.open_kit")}
            </Button>
          </Link>
        </div>
        <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center">
          <Input
            readOnly
            value={link}
            className="flex-1 border-slate-700 bg-slate-900 font-mono text-sm text-slate-200"
            data-testid="partner-link-input"
          />
          <Button onClick={handleCopy} className="rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]" data-testid="partner-copy-btn">
            {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
            {t("partner.copy")}
          </Button>
          <Button onClick={handleShare} variant="outline" className="rounded-full border-slate-700 text-slate-200" data-testid="partner-share-btn">
            <Share2 className="mr-2 h-4 w-4" />
            {t("partner.share")}
          </Button>
        </div>
      </Card>

      <div className="grid gap-5 md:grid-cols-4">
        <StatCard icon={Wallet} label={t("partner.stat_pending")} value={`€${data.totals.pending_eur.toFixed(2)}`} testId="stat-pending" />
        <StatCard icon={TrendingUp} label={t("partner.stat_ready")} value={`€${data.totals.ready_for_payout_eur.toFixed(2)}`} testId="stat-ready" accent />
        <StatCard icon={Banknote} label={t("partner.stat_paid")} value={`€${data.totals.paid_eur.toFixed(2)}`} testId="stat-paid" />
        <StatCard icon={Sparkles} label={t("partner.stat_lifetime")} value={`€${data.totals.lifetime_eur.toFixed(2)}`} testId="stat-lifetime" />
      </div>

      {data.totals.pending_eur > 0 && (
        <div className="mt-6">
          <Button
            onClick={handlePayoutRequest}
            disabled={requesting}
            className="rounded-full bg-[#0B0E5C] font-bold text-white hover:bg-[#1A1D7A]"
            data-testid="partner-request-payout-btn"
          >
            {requesting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Banknote className="mr-2 h-4 w-4" />}
            {t("partner.request_payout")}
          </Button>
        </div>
      )}

      <section className="mt-10">
        <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          {t("partner.commissions_title")}
        </h2>
        {data.commissions.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="partner-no-commissions">{t("partner.no_commissions")}</p>
        ) : (
          <Card className="overflow-hidden border-slate-800 bg-[#0A0C1F] p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-3">{t("partner.col_user")}</th>
                  <th className="px-5 py-3">{t("partner.col_plan")}</th>
                  <th className="px-5 py-3">{t("partner.col_amount")}</th>
                  <th className="px-5 py-3">{t("partner.col_commission")}</th>
                  <th className="px-5 py-3">{t("partner.col_status")}</th>
                  <th className="px-5 py-3">{t("partner.col_date")}</th>
                </tr>
              </thead>
              <tbody>
                {data.commissions.map((c) => {
                  const badge = STATUS_BADGE[c.status] || STATUS_BADGE.pending;
                  return (
                    <tr key={c.commission_id} className="border-b border-slate-800/60" data-testid={`commission-row-${c.commission_id}`}>
                      <td className="px-5 py-3 font-mono text-slate-300">{c.referred_email_masked}</td>
                      <td className="px-5 py-3 font-semibold text-white">{TIER_LABEL[c.plan_id]}</td>
                      <td className="px-5 py-3 font-mono text-slate-400">€{(c.payment_amount || 0).toFixed(2)}</td>
                      <td className="px-5 py-3 font-mono font-bold text-[#9ACD32]">€{(c.commission_amount || 0).toFixed(2)}</td>
                      <td className="px-5 py-3"><span className={`tier-badge ${badge.cls}`}>{t(badge.labelKey)}</span></td>
                      <td className="px-5 py-3 text-slate-400">{new Date(c.created_at).toLocaleDateString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId, accent }) {
  return (
    <Card className={`border-slate-800 bg-[#0A0C1F] p-5 ${accent ? "ring-1 ring-[#9ACD32]/30" : ""}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className={`h-4 w-4 ${accent ? "text-[#9ACD32]" : "text-slate-500"}`} />
      </div>
      <div className="mt-3 font-display text-3xl font-extrabold text-white">{value}</div>
    </Card>
  );
}
